import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import WorkbenchPreviewImage from '@/components/ipe/WorkbenchPreviewImage';
import {
  applyStampImagesToExportRow,
  applyTitleRoleToExportRow,
  stampSourceKindLabel,
  titleSourceKindLabel,
} from '@/pages/ipe/workbench/utils/exportStampRoleMeta';
import '@/styles/ipe/workbench/modals/ExportPreviewModal.css';

/** 超过该数量才开启横向滚动，视口约容纳 3 张缩略图 */
const THUMB_SCROLL_THRESHOLD = 3;

/**
 * 缩略图横向条：>3 张时限制可视宽度，并绘制始终可见的自定义滚动条
 * （不依赖系统原生 scrollbar，避免 Windows overlay 隐藏）
 */
function ThumbImageStrip({
  items,
  itemClassName,
  imgClassName,
  stripClassName,
  emptyLabel,
  ariaLabel,
  getKey,
  getUrl,
  getAlt,
  getTitle,
  renderBadge,
  onItemClick,
}) {
  const { t } = useTranslation('workbench');
  const scrollerRef = useRef(null);
  const trackRef = useRef(null);
  const dragRef = useRef(null);
  const [bar, setBar] = useState({ show: false, ratio: 1, leftRatio: 0 });

  const syncBar = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const show = maxScroll > 1 && items.length > THUMB_SCROLL_THRESHOLD;
    const ratio = el.scrollWidth > 0 ? el.clientWidth / el.scrollWidth : 1;
    const leftRatio = maxScroll > 0 ? el.scrollLeft / maxScroll : 0;
    setBar({
      show,
      ratio: Math.min(1, Math.max(0.2, ratio)),
      leftRatio: Math.min(1, Math.max(0, leftRatio)),
    });
  }, [items.length]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    syncBar();
    const onScroll = () => syncBar();
    el.addEventListener('scroll', onScroll, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => syncBar())
      : null;
    ro?.observe(el);
    window.addEventListener('resize', syncBar);
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro?.disconnect();
      window.removeEventListener('resize', syncBar);
    };
  }, [syncBar, items]);

  const scrollable = items.length > THUMB_SCROLL_THRESHOLD;

  const onTrackPointerDown = (event) => {
    if (!bar.show || event.button !== 0) return;
    const track = trackRef.current;
    const scroller = scrollerRef.current;
    if (!track || !scroller) return;
    event.preventDefault();
    const trackRect = track.getBoundingClientRect();
    const thumbWidth = trackRect.width * bar.ratio;
    const maxThumbLeft = Math.max(0, trackRect.width - thumbWidth);
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const pointerOffset = event.clientX - trackRect.left - thumbWidth / 2;
    const nextLeft = Math.min(maxThumbLeft, Math.max(0, pointerOffset));
    scroller.scrollLeft = maxThumbLeft > 0 ? (nextLeft / maxThumbLeft) * maxScroll : 0;

    dragRef.current = {
      startX: event.clientX,
      startScroll: scroller.scrollLeft,
      maxScroll,
      maxThumbLeft,
      trackWidth: trackRect.width,
      thumbWidth,
    };
    const onMove = (ev) => {
      const drag = dragRef.current;
      if (!drag || !scrollerRef.current) return;
      const dx = ev.clientX - drag.startX;
      const scrollDx = drag.maxThumbLeft > 0
        ? (dx / drag.maxThumbLeft) * drag.maxScroll
        : 0;
      scrollerRef.current.scrollLeft = Math.min(
        drag.maxScroll,
        Math.max(0, drag.startScroll + scrollDx),
      );
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  if (items.length === 0) {
    return <span className="export-preview-modal__empty">{emptyLabel}</span>;
  }

  const thumbLeftPercent = (1 - bar.ratio) * bar.leftRatio * 100;
  const thumbWidthPercent = bar.ratio * 100;

  return (
    <div className={`export-preview-thumb-strip${scrollable ? ' export-preview-thumb-strip--scrollable' : ''}`}>
      <div
        ref={scrollerRef}
        className={`${stripClassName}${scrollable ? ` ${stripClassName}--scroll` : ''}`}
        role="list"
        aria-label={ariaLabel}
      >
        {items.map((item, index) => {
          const badge = renderBadge?.(item, index);
          const clickable = typeof onItemClick === 'function';
          const ItemTag = clickable ? 'button' : 'div';
          return (
            <ItemTag
              key={getKey(item, index)}
              type={clickable ? 'button' : undefined}
              className={`${itemClassName}${clickable ? ` ${itemClassName}--action` : ''}`}
              role={clickable ? undefined : 'listitem'}
              title={getTitle?.(item, index)}
              onClick={clickable ? () => onItemClick(item, index) : undefined}
            >
              <WorkbenchPreviewImage
                url={getUrl(item)}
                alt={getAlt(item, index)}
                className={imgClassName}
              />
              {badge ? <span className="export-preview-role-badge">{badge}</span> : null}
            </ItemTag>
          );
        })}
      </div>
      {bar.show ? (
        <div
          ref={trackRef}
          className="export-preview-thumb-scrollbar"
          role="scrollbar"
          aria-orientation="horizontal"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(bar.leftRatio * 100)}
          aria-label={t('exportPreview.scrollAria')}
          onPointerDown={onTrackPointerDown}
        >
          <div
            className="export-preview-thumb-scrollbar__thumb"
            style={{
              width: `${thumbWidthPercent}%`,
              left: `${thumbLeftPercent}%`,
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

function ResultThumbnails({
  images = [],
  emptyLabel,
  altPrefix,
  renderBadge,
}) {
  const { t } = useTranslation('workbench');
  const list = Array.isArray(images) ? images.filter((item) => item?.url) : [];
  const resolvedEmptyLabel = emptyLabel || t('exportPreview.emptyImages');
  const resolvedAltPrefix = altPrefix || t('exportPreview.colResults');

  return (
    <ThumbImageStrip
      items={list}
      stripClassName="export-preview-results"
      itemClassName="export-preview-results__item"
      imgClassName="export-preview-results__img"
      emptyLabel={resolvedEmptyLabel}
      ariaLabel={t('exportPreview.altCount', { prefix: resolvedAltPrefix, count: list.length })}
      getKey={(img, index) => `${img.imageId || img.url}-${index}`}
      getUrl={(img) => img.url}
      getAlt={(img, index) => t('exportPreview.altItem', { prefix: resolvedAltPrefix, index: index + 1 })}
      getTitle={(img, index) => {
        if (img.kind === 'main') return t('exportPreview.mainImageNth', { index: index + 1 });
        if (img.kind === 'detail') return t('exportPreview.detailImageNth', { index: index + 1 });
        return t('exportPreview.altItem', { prefix: resolvedAltPrefix, index: index + 1 });
      }}
      renderBadge={renderBadge}
    />
  );
}

function StampRoleCell({ stamps = [], fallbackUrl, fallbackAlt, onCycleStamp }) {
  const { t } = useTranslation('workbench');
  const list = Array.isArray(stamps) && stamps.length > 0
    ? stamps.filter((item) => item?.url)
    : (fallbackUrl ? [{ url: fallbackUrl, name: fallbackAlt || t('exportPreview.colStamps'), activeSourceKind: 'direct_upload' }] : []);

  const canCycleAny = list.some((s) => Array.isArray(s.candidates) && s.candidates.length > 1);

  return (
    <div className="export-preview-stamp-role">
      <ThumbImageStrip
        items={list}
        stripClassName="export-preview-stamps"
        itemClassName="export-preview-stamps__item"
        imgClassName="export-preview-stamps__img"
        emptyLabel={t('exportPreview.emptyStamps')}
        ariaLabel={t('exportPreview.altCount', { prefix: t('exportPreview.colStamps'), count: list.length })}
        getKey={(stamp, index) => `${stamp.imageId || stamp.url}-${stamp.activeSourceKind || ''}-${index}`}
        getUrl={(stamp) => stamp.url}
        getAlt={(stamp, index) => stamp.name || t('exportPreview.stampNth', { index: index + 1 })}
        getTitle={(stamp, index) => {
          const label = stamp.sourceLabel
            || stampSourceKindLabel(stamp.activeSourceKind)
            || t('exportStampRole.stampMaterial');
          const switchHint = Array.isArray(stamp.candidates) && stamp.candidates.length > 1
            ? t('exportPreview.switchHint')
            : '';
          return `${stamp.name || t('exportPreview.stampNth', { index: index + 1 })} · ${label}${switchHint}`;
        }}
        renderBadge={(stamp) => (
          stamp.sourceLabel
          || stampSourceKindLabel(stamp.activeSourceKind)
          || t('exportStampRole.stampMaterial')
        )}
        onItemClick={
          canCycleAny && typeof onCycleStamp === 'function'
            ? (stamp, index) => {
              if (!Array.isArray(stamp.candidates) || stamp.candidates.length < 2) return;
              onCycleStamp(index);
            }
            : undefined
        }
      />
      {canCycleAny ? (
        <p className="export-preview-stamp-role__hint">{t('exportPreview.stampHint')}</p>
      ) : null}
    </div>
  );
}

function TitleRoleCell({
  titleRole,
  fallbackTitle = '',
  stampName = '',
  onChangeTitle,
}) {
  const { t } = useTranslation('workbench');
  const role = titleRole && typeof titleRole === 'object' ? titleRole : null;
  const value = String(role?.title ?? fallbackTitle ?? '').trim();
  const sourceLabel = role?.sourceLabel
    || titleSourceKindLabel(role?.sourceKind)
    || (value ? t('exportStampRole.aiCopywriting') : t('exportStampRole.unlinked'));
  const display = value || t('exportStampRole.noTitle');

  return (
    <div className="export-preview-title-role">
      <div className="export-preview-title-role__meta">
        <span className={`export-preview-title-role__badge export-preview-title-role__badge--${role?.sourceKind || 'none'}`}>
          {sourceLabel}
        </span>
        {stampName ? (
          <span className="export-preview-modal__name-tag export-preview-modal__name-tag--muted" title={stampName}>
            {stampName}
          </span>
        ) : null}
      </div>
      {typeof onChangeTitle === 'function' ? (
        <textarea
          className="export-preview-title-role__input"
          rows={3}
          value={value}
          placeholder={t('exportPreview.titlePh')}
          aria-label={t('exportPreview.titleSlotAria')}
          onChange={(e) => onChangeTitle(e.target.value)}
        />
      ) : (
        <span className="export-preview-modal__name-text" title={display}>{display}</span>
      )}
    </div>
  );
}

/**
 * previewData 行（方案二显式槽位）：
 * - resultImages：成果图
 * - stampImages[]：印花图（带 sourceKind / candidates）
 * - originalImages[]：原图（血缘，可选）
 * - titleRole / title：标题（绑定该组成品的 AI 文案，可改）
 */
export default function ExportPreviewModal({
  isOpen,
  onCancel,
  onExport,
  previewData = [],
}) {
  const { t } = useTranslation('workbench');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [rowOverrides, setRowOverrides] = useState({});
  const [exporting, setExporting] = useState(false);
  const exportingRef = useRef(false);

  useEffect(() => {
    if (isOpen && previewData.length > 0) {
      setSelectedIds(new Set(previewData.map((item) => item.id)));
      setRowOverrides({});
      return;
    }
    if (!isOpen) {
      setSelectedIds(new Set());
      setRowOverrides({});
    }
  }, [isOpen, previewData]);

  const rows = useMemo(() => (
    (previewData || []).map((item) => {
      const override = rowOverrides[item.id];
      return override ? { ...item, ...override } : item;
    })
  ), [previewData, rowOverrides]);

  const selectedCount = selectedIds.size;
  const allSelected = rows.length > 0 && rows.every((item) => selectedIds.has(item.id));
  const someSelected = rows.some((item) => selectedIds.has(item.id));

  if (!isOpen) return null;

  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(rows.map((item) => item.id)));
  };

  const handleToggleItem = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const mergeRowOverride = (rowId, patch) => {
    setRowOverrides((prev) => {
      const base = rows.find((r) => r.id === rowId) || previewData.find((r) => r.id === rowId);
      if (!base) return prev;
      return {
        ...prev,
        [rowId]: {
          ...(prev[rowId] || {}),
          ...patch,
        },
      };
    });
  };

  const handleCycleStamp = (rowId, stampIndex) => {
    setRowOverrides((prev) => {
      const base = rows.find((r) => r.id === rowId) || previewData.find((r) => r.id === rowId);
      if (!base) return prev;
      const stamps = Array.isArray(base.stampImages) ? [...base.stampImages] : [];
      const stamp = stamps[stampIndex];
      if (!stamp || !Array.isArray(stamp.candidates) || stamp.candidates.length < 2) return prev;
      const currentUrl = String(stamp.url || '').trim();
      const curIdx = stamp.candidates.findIndex((c) => String(c.url || '').trim() === currentUrl);
      const nextCand = stamp.candidates[(curIdx + 1) % stamp.candidates.length];
      if (!nextCand?.url) return prev;
      stamps[stampIndex] = {
        ...stamp,
        url: nextCand.url,
        imageId: nextCand.imageId || stamp.imageId,
        activeSourceKind: nextCand.sourceKind,
        sourceLabel: nextCand.label || stampSourceKindLabel(nextCand.sourceKind),
      };
      const patched = applyStampImagesToExportRow(base, stamps);
      return {
        ...prev,
        [rowId]: {
          ...(prev[rowId] || {}),
          stampImages: patched.stampImages,
          stampImageUrl: patched.stampImageUrl,
          stampImageId: patched.stampImageId,
          stampName: patched.stampName,
        },
      };
    });
  };

  const handleTitleChange = (rowId, nextTitle) => {
    const base = rows.find((r) => r.id === rowId) || previewData.find((r) => r.id === rowId);
    if (!base) return;
    const patched = applyTitleRoleToExportRow(base, {
      title: nextTitle,
      sourceKind: String(nextTitle || '').trim() ? 'manual' : 'none',
    });
    mergeRowOverride(rowId, {
      title: patched.title,
      titleLanguage: patched.titleLanguage,
      displayName: patched.displayName,
      titleRole: patched.titleRole,
    });
  };

  const handleExport = async () => {
    if (exportingRef.current) return;
    const selectedData = rows.filter((item) => selectedIds.has(item.id));
    if (!onExport || selectedData.length === 0) return;
    exportingRef.current = true;
    setExporting(true);
    try {
      await Promise.resolve(onExport(selectedData));
    } finally {
      exportingRef.current = false;
      setExporting(false);
    }
  };

  return createPortal(
    <div className="funding-modal funding-modal--top export-preview-modal-layer" data-ipe-blocks-selection-bar="">
      <div className="funding-modal__backdrop" role="presentation" onClick={onCancel} />
      <div
        className="funding-modal__panel funding-modal__panel--unified export-preview-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-preview-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="export-preview-modal__header">
          <div>
            <h3 id="export-preview-title">{t('exportPreview.title')}</h3>
            <p className="export-preview-modal__subtitle">
              {t('exportPreview.subtitle')}
            </p>
          </div>
          <button
            type="button"
            className="export-preview-modal__close"
            onClick={onCancel}
            aria-label={t('exportPreview.close')}
          >
            <X size={18} />
          </button>
        </header>

        <div className="export-preview-modal__body">
          <div className="export-preview-modal__table-wrap">
            <table className="export-preview-modal__table export-preview-modal__table--roles">
              <thead>
                <tr>
                  <th scope="col" className="export-preview-modal__col-check">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={handleToggleAll}
                      ref={(el) => {
                        if (el) el.indeterminate = !allSelected && someSelected;
                      }}
                      aria-label={t('exportPreview.selectAll')}
                    />
                  </th>
                  <th scope="col" className="export-preview-modal__col-results">{t('exportPreview.colResults')}</th>
                  <th scope="col" className="export-preview-modal__col-stamps">{t('exportPreview.colStamps')}</th>
                  <th scope="col" className="export-preview-modal__col-originals">{t('exportPreview.colOriginals')}</th>
                  <th scope="col" className="export-preview-modal__col-name">{t('exportPreview.colTitle')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => {
                  const isSelected = selectedIds.has(item.id);
                  const noTitle = t('exportPreview.noTitle');
                  const displayName = String(item.displayName || item.title || noTitle).trim() || noTitle;
                  const resultImages = Array.isArray(item.resultImages) && item.resultImages.length > 0
                    ? item.resultImages
                    : [
                      ...(Array.isArray(item.mainImages) ? item.mainImages : []),
                      ...(Array.isArray(item.detailImages) ? item.detailImages : []),
                    ];
                  const originalImages = Array.isArray(item.originalImages) ? item.originalImages : [];
                  return (
                    <tr key={item.id} className={isSelected ? 'is-selected' : ''}>
                      <td className="export-preview-modal__col-check">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleItem(item.id)}
                          aria-label={t('exportPreview.selectRow', { name: displayName })}
                        />
                      </td>
                      <td className="export-preview-modal__col-results">
                        <ResultThumbnails
                          images={resultImages}
                          emptyLabel={t('exportPreview.emptyResults')}
                          altPrefix={t('exportPreview.colResults')}
                          renderBadge={(img) => {
                            if (img.kind === 'main') return t('exportPreview.badgeMain');
                            if (img.kind === 'detail') return t('exportPreview.badgeDetail');
                            return t('exportPreview.badgeProduct');
                          }}
                        />
                      </td>
                      <td className="export-preview-modal__col-stamps">
                        <StampRoleCell
                          stamps={item.stampImages}
                          fallbackUrl={item.stampImageUrl}
                          fallbackAlt={item.stampName}
                          onCycleStamp={(stampIndex) => handleCycleStamp(item.id, stampIndex)}
                        />
                      </td>
                      <td className="export-preview-modal__col-originals">
                        <ResultThumbnails
                          images={originalImages}
                          emptyLabel={t('exportPreview.emptyOriginals')}
                          altPrefix={t('exportPreview.colOriginals')}
                          renderBadge={() => t('exportPreview.badgeOriginal')}
                        />
                      </td>
                      <td className="export-preview-modal__col-name">
                        <TitleRoleCell
                          titleRole={item.titleRole}
                          fallbackTitle={item.title || ''}
                          stampName={item.stampName || ''}
                          onChangeTitle={(next) => handleTitleChange(item.id, next)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <footer className="export-preview-modal__footer">
          <div className="export-preview-modal__actions">
            <button type="button" className="funding-button funding-button--ghost" onClick={onCancel}>
              {t('exportPreview.cancel')}
            </button>
            <button
              type="button"
              className="funding-button funding-button--accent"
              onClick={handleExport}
              disabled={selectedCount === 0 || exporting}
            >
              {exporting ? t('exportPreview.exporting') : t('exportPreview.export')}
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
